<script lang="ts">
  import * as H from "../settings/hints.ts";
  import { proposalFields } from "@split/core";
  import { tutoring } from "../lib/tutoring.svelte.ts";
  import { aiQueue } from "../lib/ai/queue.svelte.ts";
  import { tick } from "svelte";
  import { assistant, SCOPES, type ChatScope } from "../lib/ai/assistant.svelte.ts";
  import { renderMarkdown } from "../lib/ai/markdown.ts";
  import { app } from "../lib/store.svelte.ts";
  import Drawer from "../components/Drawer.svelte";
  import { shortDateTime } from "../lib/format.ts";
  import { isTestsGroup } from "@split/core";
  import { router } from "../lib/router.svelte.ts";
  import { groupPath } from "../lib/routes.ts";

  // The receipt test bench lives in the Tests group, so real groups stay clean.
  async function openTests() {
    let g = app.groups.find((x) => isTestsGroup(x.name));
    if (!g) {
      await app.createGroup("Tests");
      g = app.groups.find((x) => isTestsGroup(x.name));
    }
    if (g) router.go(groupPath(g.id, "trips"));
  }

  let chatsOpen = $state(false);
  let chatQuery = $state("");
  let editing = $state<string | null>(null);
  let editText = $state("");
  let renaming = $state<string | null>(null);
  let renameText = $state("");
  const shownChats = $derived(
    assistant.chats.filter((c) => !chatQuery.trim() || c.title.toLowerCase().includes(chatQuery.trim().toLowerCase())),
  );
  const lastUserId = $derived([...assistant.messages].reverse().find((m) => m.role === "user")?.id);
  const lastBotId = $derived([...assistant.messages].reverse().find((m) => m.role === "assistant")?.id);

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      app.toast("Copied");
    } catch {
      app.toast("Couldn't copy", "error");
    }
  }

  function startEdit(id: string, text: string) {
    editing = id;
    editText = text;
  }

  async function saveEdit() {
    const t = editText;
    editing = null;
    await assistant.editLast(t);
  }

  function pick(id: string) {
    assistant.openChat(id);
    chatsOpen = false;
  }

  const secs = (ms?: number) => (ms ? `${Math.max(1, Math.round(ms / 1000))}s` : "");

  let draft = $state("");
  let showSettings = $state(false);
  let list: HTMLElement | undefined = $state();
  let input: HTMLTextAreaElement | undefined = $state();

  // Settings form (edited as text, saved on Save).
  let baseUrl = $state(assistant.settings.baseUrl);
  let apiKey = $state(assistant.settings.apiKey);
  let model = $state(assistant.settings.model);

  $effect(() => {
    if (app.groupId) assistant.open(app.groupId);
  });
  // An empty chat takes the scope of the space you came from.
  $effect(() => {
    if (assistant.messages.length === 0) assistant.scope = assistant.defaultScope;
  });

  $effect(() => {
    if (assistant.connection.status === "unknown") void assistant.check();
  });

  // The owner's tutoring records, so the assistant can help with sessions and the calendar.
  $effect(() => {
    if (app.role === "owner" && !tutoring.loaded) void tutoring.load();
  });

  // Keep the newest message in view while the reply streams in.
  $effect(() => {
    void assistant.messages.length;
    const last = assistant.messages[assistant.messages.length - 1];
    void (last && last.role === "assistant" ? last.text : "");
    void tick().then(() => list?.scrollTo({ top: list.scrollHeight }));
  });

  const ownerName = $derived(
    app.current.settings.ownerPersonId ? app.current.people[app.current.settings.ownerPersonId]?.name : null,
  );
  const someone = $derived(
    app.ledger.people.find((p) => p.status === "owes")?.name ?? app.ledger.people.find((p) => p.status !== "you")?.name ?? "Emily",
  );
  const pair = $derived.by(() => {
    const names = app.ledger.people.filter((p) => p.status !== "you").map((p) => p.name);
    return names.length >= 2 ? `${names[0]} and ${names[1]}` : "Emily and Chloe";
  });
  const suggestions = $derived([
    "Who still owes me, and for what?",
    `What has ${someone} bought this month?`,
    `Add a rotisserie chicken for ${pair}`,
    `Log $20 from ${someone} on Zelle`,
  ]);

  async function send(text = draft) {
    if (!text.trim()) return;
    draft = "";
    await assistant.send(text);
    input?.focus();
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      void send();
    }
  }

  async function saveSettings(e: SubmitEvent) {
    e.preventDefault();
    assistant.saveSettings({ baseUrl, apiKey, model });
    await assistant.check();
    model = assistant.settings.model;
    if (assistant.connection.status === "ok") showSettings = false;
  }

  const status = $derived.by(() => {
    const c = assistant.connection;
    if (c.status === "ok") return { text: `Connected to ${assistant.settings.model || "your model"}`, tone: "ok" };
    if (c.status === "checking") return { text: "Checking LM Studio…", tone: "busy" };
    if (c.status === "down") return { text: "Can't reach LM Studio", tone: "down" };
    return { text: "Not checked yet", tone: "busy" };
  });
</script>

<div class="ask-wrap">
<aside class="chats-side" aria-label="Chats">
  <h2>Chats</h2>
  {@render chatList()}
</aside>
<div class="page ask">
  <div class="page-head">
    <div>
      <h1>Ask</h1>
      <p class="lede">Chat with the model on your computer. It can look things up, and it asks before changing anything.</p>
      <div class="scope" role="group" aria-label="What this chat is about">
        <label>
          <span>Category</span>
          <select class="input" value={assistant.scope} onchange={(e) => assistant.setScope((e.currentTarget as HTMLSelectElement).value as ChatScope)} aria-label="Category" disabled={assistant.running}>
            {#each SCOPES.filter((x) => x.id !== "tutoring" || app.role === "owner").filter((x) => x.id !== "money" || app.role === "owner") as sc (sc.id)}
              <option value={sc.id}>{sc.label}</option>
            {/each}
          </select>
        </label>
        {#if assistant.scope === "group" || assistant.scope === "everything"}
          <label>
            <span>Group</span>
            <select class="input" value={app.groupId} onchange={(e) => router.go(groupPath((e.currentTarget as HTMLSelectElement).value, "ask"))} aria-label="Group" disabled={assistant.running || app.groups.length < 2}>
              {#each app.groups as g (g.id)}<option value={g.id}>{g.name}</option>{/each}
            </select>
          </label>
        {/if}
        <span class="muted small">{assistant.scopePicked ? SCOPES.find((x) => x.id === assistant.scope)?.hint : "Picked from your question; change it any time."}</span>
      </div>
    </div>
    <div class="head-actions">
      <button class="pill {status.tone}" onclick={() => (showSettings = !showSettings)} aria-expanded={showSettings}>
        <span class="dot" aria-hidden="true"></span>{status.text}
      </button>
      <button class="btn small chats-btn" onclick={() => (chatsOpen = true)}>Chats{assistant.chats.length ? ` (${assistant.chats.length})` : ""}</button>
      <button class="btn small" onclick={() => assistant.newChat()} disabled={assistant.messages.length === 0}>New chat</button>
    </div>
  </div>

  {#if showSettings || assistant.connection.status === "down"}
    <section class="setup">
      {#if assistant.connection.status === "down"}
        <p class="problem">{assistant.connection.message}</p>
      {/if}
      <form class="settings" onsubmit={saveSettings}>
        <label class="field grow">
          <span>Server address</span>
          <input class="input" bind:value={baseUrl} placeholder="http://localhost:1234/v1" autocomplete="off" />
        </label>
        <label class="field">
          <span>Model</span>
          {#if assistant.connection.models.length}
            <select class="input" bind:value={model}>
              {#each assistant.connection.models as m}<option value={m}>{m}</option>{/each}
            </select>
          {:else}
            <input class="input" bind:value={model} placeholder="Picked automatically" />
          {/if}
        </label>
        <label class="field">
          <span>API key (optional)</span>
          <input class="input" type="password" bind:value={apiKey} autocomplete="off" />
        </label>
        <button class="btn primary">Save and test</button>
      </form>
      <div class="bg">
        <label class="field"
          ><span>Background AI work</span>
          <select class="input" value={aiQueue.mode} onchange={(e) => aiQueue.setMode((e.currentTarget as HTMLSelectElement).value as "off" | "idle" | "overnight")}>
            <option value="idle">When I'm idle</option>
            <option value="overnight">Overnight only (11pm–7am)</option>
            <option value="off">Off</option>
          </select></label
        >
        <p class="muted small">
          Catalog lookups, rewards rates and money suggestions run in the background on this device. Anything you ask goes first: background work stops and picks up later.
          {#if aiQueue.current}<strong>Now: {aiQueue.current}.</strong>{/if}
        </p>
      </div>
      {#if app.role === "owner"}
        <p class="muted small">
          Check how well this model reads receipts on four sample Costco receipts:
          <button class="link-btn" onclick={openTests}>{app.groups.some((g) => isTestsGroup(g.name)) ? "Open the receipt test bench" : "Make the Tests group with the receipt test bench"}</button>
          (only in your Tests group).
        </p>
      {/if}
      <details class="help" open={assistant.connection.status === "down"}>
        <summary>Set up LM Studio (one time)</summary>
        <ol>
          <li>In LM Studio, download and load a model that supports tool use, such as a Qwen 3 or Llama 3.1+ model.</li>
          <li>Open the <strong>Developer</strong> tab and turn on the server (it runs at <code>http://localhost:1234</code>).</li>
          <li>In the server settings, turn on <strong>Enable CORS</strong>, so this page is allowed to talk to it.</li>
          <li>Use this page in Chrome or Edge on the same computer. If the browser asks to let this site use devices on your local network, choose <strong>Allow</strong>.</li>
          <li>Come back here and tap <strong>Save and test</strong>.</li>
        </ol>
        {#if H.ASK_PRIVACY_NOTE.trim()}<p class="muted">{H.ASK_PRIVACY_NOTE}</p>{/if}
      </details>
    </section>
  {/if}

  <div class="chat" bind:this={list} aria-live="polite">
    {#if assistant.messages.length === 0}
      <div class="empty-chat">
        <p class="hello">{ownerName ? `Hi ${ownerName}.` : "Hi."} Ask about balances, items, receipts or history, or tell it what to add.{#if app.role === "owner" && tutoring.students.length} It can also look over tutoring and schedule or log sessions.{/if}</p>
        <div class="suggest">
          {#each suggestions as s}<button class="btn small" onclick={() => send(s)} disabled={assistant.running}>{s}</button>{/each}
        </div>
      </div>
    {/if}

    {#each assistant.messages as m (m.id)}
      {#if m.role === "user"}
        {#if editing === m.id}
          <form class="msg user editing" onsubmit={(e) => { e.preventDefault(); void saveEdit(); }}>
            <textarea bind:value={editText} rows="2" aria-label="Edit your question"></textarea>
            <div class="row-actions">
              <button type="button" class="btn small quiet" onclick={() => (editing = null)}>Cancel</button>
              <button class="btn small primary" disabled={!editText.trim()}>Ask again</button>
            </div>
          </form>
        {:else}
          <div class="msg user"><p>{m.text}</p></div>
          {#if m.id === lastUserId && !assistant.running}
            <div class="tools-row right"><button class="mini" onclick={() => startEdit(m.id, m.text)}>Edit</button></div>
          {/if}
        {/if}
      {:else if m.role === "assistant"}
        <div class="msg bot" class:streaming={m.streaming && !!m.text}>
          {#if m.reasoning}
            <!-- Open while it thinks; after that only you open or close it (it never closes itself). -->
            <details class="thinking" open={m.thinkingOpen ?? false} ontoggle={(e) => (m.thinkingOpen = (e.currentTarget as HTMLDetailsElement).open)}>
              <summary>{m.streaming && !m.text ? "Thinking…" : `Thought for ${secs(m.ms) || "a moment"}`}</summary>
              <div class="thought">{m.reasoning}</div>
            </details>
          {/if}
          {#if m.text}{@html renderMarkdown(m.text)}{:else if !m.reasoning}<p class="muted">Thinking…</p>{/if}
        </div>
        {#if !m.streaming && m.text}
          <div class="tools-row">
            <button class="mini" onclick={() => copy(m.text)}>Copy</button>
            {#if m.id === lastBotId && !assistant.running}<button class="mini" onclick={() => assistant.regenerate()}>Regenerate</button>{/if}
            {#if m.ms}<span class="muted">{secs(m.ms)}</span>{/if}
          </div>
        {/if}
      {:else if m.role === "activity"}
        {#if m.tool}
          <details class="activity tool" class:failed={m.failed}>
            <summary>{m.text}</summary>
            <div class="tool-io">
              <p class="muted">Asked <code>{m.tool.name}</code></p>
              <pre>{m.tool.args}</pre>
              <p class="muted">Got back</p>
              <pre>{m.tool.result}</pre>
            </div>
          </details>
        {:else}
          <p class="activity" class:failed={m.failed}>{m.text}</p>
        {/if}
      {:else if m.role === "proposal"}
        <div class="card {m.status}">
          <p class="summary">{m.summary}</p>
          {#if m.status === "pending"}
            {@const fields = m.api ? m.api.fields : proposalFields(m.commands)}
            {#if fields.length}
              <div class="fix">
                {#each fields as f (f.key)}
                  <label class="fix-field" class:wide={f.kind === "text"}>
                    <span>{f.label}</span>
                    <span class="fix-input">
                      {#if f.kind === "money"}<span class="pre">$</span>{/if}
                      <input
                        class="input"
                        type={f.kind === "date" ? "date" : "text"}
                        inputmode={f.kind === "money" || f.kind === "percent" ? "decimal" : undefined}
                        value={m.edits?.[f.key] ?? f.value}
                        oninput={(e) => (m.edits = { ...(m.edits ?? {}), [f.key]: (e.currentTarget as HTMLInputElement).value })}
                      />
                      {#if f.kind === "percent"}<span class="post">%</span>{/if}
                    </span>
                  </label>
                {/each}
              </div>
              {#if H.ASK_FIX_NOTE.trim()}<p class="muted small">{H.ASK_FIX_NOTE}</p>{/if}
            {/if}
            <div class="actions">
              <button class="btn primary small" onclick={() => assistant.confirm(m.id)} disabled={app.readOnly}>Yes, save it</button>
              <button class="btn small" onclick={() => assistant.decline(m.id)}>No</button>
            </div>
            {#if app.readOnly}<p class="muted small">Return to now to save changes.</p>{/if}
          {:else if m.status === "applied"}
            <p class="state ok">{m.api ? (m.api.calls.some((c) => c.table === "money") ? "Saved to Money." : "Saved to Tutoring.") : "Saved. It's in History, where you can undo it."}</p>
          {:else if m.status === "declined"}
            <p class="state">Not saved.</p>
          {:else}
            <p class="state bad">Couldn't save: {m.error}</p>
          {/if}
        </div>
      {:else}
        <p class="error-line" role="alert">{m.text}</p>
      {/if}
    {/each}
  </div>

  <form class="composer" onsubmit={(e) => { e.preventDefault(); void send(); }}>
    <textarea
      bind:this={input}
      bind:value={draft}
      rows="1"
      placeholder="Ask about the ledger, or say what to add"
      onkeydown={onKey}
      aria-label="Message"
    ></textarea>
    {#if assistant.running}
      <button type="button" class="btn" onclick={() => assistant.stop()}>Stop</button>
    {:else}
      <button class="btn primary" disabled={!draft.trim()}>Send</button>
    {/if}
  </form>
</div>
</div>

{#if chatsOpen}
  <Drawer title="Chats" onclose={() => (chatsOpen = false)}>
    {@render chatList()}
  </Drawer>
{/if}

{#snippet chatList()}
    <div class="chat-list">
      <button class="btn primary" onclick={() => { assistant.newChat(); chatsOpen = false; }}>New chat</button>
      <input class="input" type="search" bind:value={chatQuery} placeholder="Search chats" aria-label="Search chats" />
      {#each shownChats as c (c.id)}
        <div class="chat-item" class:on={c.id === assistant.chatId}>
          {#if renaming === c.id}
            <form onsubmit={(e) => { e.preventDefault(); assistant.renameChat(c.id, renameText); renaming = null; }}>
              <input class="input" bind:value={renameText} aria-label="Chat name" />
            </form>
          {:else}
            <button class="open" onclick={() => pick(c.id)}>
              <span class="t">{c.title}</span>
              <span class="muted small">{shortDateTime(c.updatedAt)} · {c.count} messages</span>
            </button>
          {/if}
          <span class="ops">
            <button class="mini" onclick={() => { renaming = c.id; renameText = c.title; }}>Rename</button>
            <button class="mini danger" onclick={() => assistant.deleteChat(c.id)}>Delete</button>
          </span>
        </div>
      {:else}
        <p class="muted">{chatQuery ? "No chats match." : "Your chats in this group show up here."}</p>
      {/each}
      {#if H.ASK_CHATS_NOTE.trim()}<p class="muted small">{H.ASK_CHATS_NOTE}</p>{/if}
    </div>
{/snippet}

<style>
  .bg {
    display: grid;
    grid-template-columns: minmax(0, 260px) 1fr;
    gap: 12px;
    align-items: end;
    margin: 12px 0;
  }

  @media (max-width: 640px) {
    .bg {
      grid-template-columns: 1fr;
    }
  }

  .ask-wrap {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
  }

  .chats-side {
    display: none;
  }

  @media (min-width: 1180px) {
    .ask-wrap {
      grid-template-columns: 260px minmax(0, 1fr);
      gap: 24px;
      align-items: start;
    }

    .chats-side {
      display: block;
      position: sticky;
      top: 16px;
      max-height: calc(100vh - 32px);
      overflow-y: auto;
      padding: 16px 0 16px 4px;
    }

    .chats-side h2 {
      margin: 8px 0 12px;
      font-size: var(--t-lg);
    }

    .chats-btn {
      display: none;
    }
  }

  .fix {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 8px 0 4px;
  }

  .fix-field {
    display: grid;
    gap: 2px;
    flex: 0 1 120px;
    font-size: var(--t-xs);
    color: var(--muted);
  }

  .fix-field.wide {
    flex: 1 1 200px;
  }

  .fix-input {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .fix-input .input {
    min-height: 32px;
    padding: 4px 8px;
    font-size: var(--t-sm);
    color: var(--ink);
  }

  .thinking {
    margin-bottom: 8px;
    padding: 6px 10px;
    border-left: 3px solid var(--rule-strong);
    border-radius: 4px;
    background: var(--wash);
    font-size: var(--t-sm);
  }

  .thinking summary {
    cursor: pointer;
    color: var(--muted);
    font-weight: 600;
  }

  .thought {
    margin-top: 6px;
    white-space: pre-wrap;
    color: var(--ink-2);
    max-height: 320px;
    overflow-y: auto;
  }

  .tools-row {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-top: -4px;
    font-size: var(--t-xs);
  }

  .tools-row.right {
    justify-content: flex-end;
  }

  .mini {
    padding: 2px 4px;
    border: 0;
    background: none;
    color: var(--muted);
    font: inherit;
    font-size: var(--t-xs);
    font-weight: 600;
    cursor: pointer;
  }

  .mini:hover {
    color: var(--ink);
  }

  .mini.danger:hover {
    color: var(--owes);
  }

  .activity.tool::before {
    display: none;
  }

  .tool summary {
    cursor: pointer;
    list-style: none;
  }

  .tool summary::-webkit-details-marker {
    display: none;
  }

  .tool summary::before {
    content: "▸";
    display: inline-block;
    width: 14px;
    color: var(--muted);
  }

  .tool[open] summary::before {
    content: "▾";
  }

  .tool-io {
    margin: 6px 0 0 14px;
    display: grid;
    gap: 4px;
  }

  .tool-io pre {
    margin: 0;
    padding: 8px 10px;
    max-height: 220px;
    overflow: auto;
    border-radius: 6px;
    background: var(--wash);
    font-family: var(--mono);
    font-size: 0.75rem;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .editing {
    display: grid;
    gap: 8px;
    width: min(560px, 88%);
  }

  .editing textarea {
    color: var(--ink);
  }

  .row-actions {
    display: flex;
    justify-content: flex-end;
    gap: 6px;
  }

  .chat-list {
    display: grid;
    gap: 10px;
  }

  .chat-item {
    display: grid;
    gap: 2px;
    padding: 8px 10px;
    border-radius: var(--r-md);
  }

  .chat-item.on {
    background: var(--wash);
  }

  .chat-item .open {
    display: grid;
    gap: 2px;
    padding: 0;
    border: 0;
    background: none;
    color: var(--ink);
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .chat-item .t {
    font-weight: 600;
  }

  .ops {
    display: flex;
    gap: 6px;
  }

  .ask {
    display: flex;
    flex-direction: column;
    min-height: calc(100vh - 20px);
    padding-bottom: 24px;
    max-width: 860px;
  }

  .scope {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 8px;
    align-items: center;
    margin-top: 8px;
    font-size: var(--t-sm);
  }

  .scope label {
    display: inline-flex;
    gap: 6px;
    align-items: center;
  }

  .scope .input {
    width: auto;
    min-height: 32px;
    padding: 2px 8px;
  }

  .head-actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .pill {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 6px 12px;
    border-radius: 999px;
    border: 1px solid var(--rule-strong);
    background: var(--surface);
    font-size: var(--t-xs);
    font-weight: 600;
    cursor: pointer;
    max-width: 280px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--muted);
    flex: none;
  }

  .ok .dot {
    background: var(--ok);
  }

  .down .dot {
    background: var(--owes);
  }

  .busy .dot {
    background: var(--focus);
  }

  .setup {
    background: var(--surface);
    border: 1px solid var(--rule);
    border-radius: var(--r-lg);
    padding: 14px 16px;
    margin-bottom: 16px;
    display: grid;
    gap: 12px;
  }

  .problem {
    color: var(--owes);
    font-weight: 550;
    font-size: var(--t-sm);
  }

  .settings {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: flex-end;
  }

  .grow {
    flex: 1 1 240px;
  }

  .help summary {
    cursor: pointer;
    font-weight: 600;
    font-size: var(--t-sm);
  }

  .help ol {
    margin: 8px 0;
    padding-left: 20px;
    display: grid;
    gap: 4px;
    font-size: var(--t-sm);
  }

  .help code {
    font-family: var(--mono);
    font-size: 0.85em;
  }

  .help p {
    font-size: var(--t-xs);
  }

  .chat {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 4px 0 16px;
    overflow-y: auto;
    min-height: 240px;
  }

  .empty-chat {
    margin: auto 0;
    display: grid;
    gap: 12px;
    padding: 24px 0;
  }

  .hello {
    font-size: var(--t-lg);
    color: var(--ink-2);
    max-width: 52ch;
  }

  .suggest {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .suggest .btn {
    white-space: normal;
    text-align: left;
    height: auto;
    padding: 8px 12px;
  }

  .msg {
    max-width: min(640px, 88%);
    line-height: 1.5;
  }

  .msg :global(p + p),
  .msg :global(p + ul),
  .msg :global(ul + p),
  .msg :global(p + ol) {
    margin-top: 8px;
  }

  .msg :global(ul),
  .msg :global(ol) {
    margin: 4px 0 0;
    padding-left: 20px;
  }

  .msg :global(code) {
    font-family: var(--mono);
    font-size: 0.85em;
    background: var(--wash);
    padding: 1px 4px;
    border-radius: 4px;
  }

  .user {
    align-self: flex-end;
    background: var(--ink);
    color: var(--paper);
    padding: 9px 14px;
    border-radius: 16px 16px 4px 16px;
    white-space: pre-wrap;
  }

  .bot {
    align-self: flex-start;
    padding: 2px 2px;
  }

  .bot.streaming::after {
    content: "";
    display: inline-block;
    width: 7px;
    height: 1em;
    margin-left: 2px;
    vertical-align: text-bottom;
    background: var(--ink-2);
    animation: blink 1s steps(2) infinite;
  }

  @keyframes blink {
    50% {
      opacity: 0;
    }
  }

  .activity {
    font-size: var(--t-xs);
    color: var(--muted);
    padding-left: 2px;
  }

  .activity::before {
    content: "";
    display: inline-block;
    width: 6px;
    height: 6px;
    margin-right: 8px;
    border-radius: 50%;
    border: 1.5px solid var(--muted);
    vertical-align: middle;
  }

  .activity.failed {
    color: #8a5a00;
  }

  .card {
    align-self: flex-start;
    max-width: min(560px, 92%);
    background: var(--surface);
    border: 1px solid var(--rule-strong);
    border-left: 4px solid var(--focus);
    border-radius: var(--r-md);
    padding: 12px 14px;
    display: grid;
    gap: 10px;
  }

  .card.applied {
    border-left-color: var(--ok);
  }

  .card.declined {
    border-left-color: var(--rule-strong);
    opacity: 0.75;
  }

  .card.failed {
    border-left-color: var(--owes);
  }

  .summary {
    font-weight: 560;
  }

  .actions {
    display: flex;
    gap: 8px;
  }

  .state {
    font-size: var(--t-sm);
    color: var(--muted);
  }

  .state.ok {
    color: var(--ok);
  }

  .state.bad {
    color: var(--owes);
  }

  .small {
    font-size: var(--t-xs);
  }

  .error-line {
    color: var(--owes);
    font-size: var(--t-sm);
    font-weight: 550;
  }

  .composer {
    position: sticky;
    bottom: 0;
    display: flex;
    gap: 8px;
    align-items: flex-end;
    padding: 10px 0 calc(env(safe-area-inset-bottom) + 6px);
    background: var(--paper);
  }

  textarea {
    flex: 1;
    min-height: 44px;
    max-height: 160px;
    resize: none;
    padding: 11px 14px;
    border: 1px solid var(--rule-strong);
    border-radius: 14px;
    background: var(--surface);
    font-size: 16px;
    field-sizing: content;
  }

  textarea:focus {
    outline: none;
    border-color: var(--focus);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--focus) 25%, transparent);
  }

  @media (max-width: 860px) {
    .ask {
      min-height: calc(100vh - 140px);
      padding-bottom: 90px;
    }

    .composer {
      bottom: calc(env(safe-area-inset-bottom) + 64px);
    }
  }
</style>
